import { useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import { OpenTechnicianModeForm } from './OpenTechnicianModeForm'
import { useDocumentPageQuery } from '@/pages/DocumentPage/queries'
import { useTechnicianPageStore } from '../hooks/useTechnicianPageStore'
import { cn } from '@/utils'

interface OpenTechnicianModeButtonProps {
  className?: string
  disabled?: boolean
}

export const OpenTechnicianModeButton = ({
  className,
  disabled,
}: OpenTechnicianModeButtonProps) => {
  const [open, setOpen] = useState(false)
  const documentPage = useDocumentPageQuery()
  const documentId = documentPage.data?.documentId

  const reset = useTechnicianPageStore(useShallow((state) => state.reset))

  if (!documentId || documentPage.isLoading) {
    return null
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => setOpen(isOpen)}>
      <DialogTrigger
        disabled={disabled}
        className={cn(className, {
          'cursor-default': disabled,
        })}
        data-testid="open-technician-mode-button"
      >
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                className="rounded-md hover:bg-gray-100 m-0 w-auto flex p-1 items-center min-w-[50px]"
                style={{
                  backgroundColor: disabled ? '#D2E8FF' : undefined,
                }}
              >
                <div className="flex flex-col text-2xl items-center h-14">
                  👷‍♂
                  <div className="text-xs">Technician</div>
                </div>
              </Button>
            </TooltipTrigger>
            <TooltipContent className="bg-black text-white">
              <p>Technician Mode</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </DialogTrigger>
      <DialogContent
        className="min-h-56 max-w-xl gap-0 pb-0"
        ariaLabel="Enter Technician Mode"
      >
        <DialogHeader>
          <DialogTitle>Technician Mode</DialogTitle>
          <DialogDescription className="text-left max-w-[550px] self-center justify-self-center pt-4">
            Technician Mode is a guided workflow for manufacturing technicians
            to review and acknowledge work instructions before starting tasks,
            ensuring compliance and traceability.
          </DialogDescription>
        </DialogHeader>
        <div className="w-full my-6">
          <OpenTechnicianModeForm
            onStart={() => {
              reset()
              setOpen(false)
            }}
            onCancel={() => {
              setOpen(false)
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}
